import {Button, ControlledInput, TextRegular} from '@hm/fundamental'
import {useMemo, useState} from 'react'

import {formatDate} from '../../helpers'
import {useRiskSurveyTranslation} from '../../hooks'
import type {ReportLinkDto} from '../../types'

type DocumentLinksSectionProps = {
  links: ReportLinkDto[]
  canManageLinks: boolean
  isAddingLink: boolean
  isDeletingLink: boolean
  onAddLink: (data: {url: string; label: string}) => void
  onDeleteLink: (linkId: string) => void
}

const isHttpsUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value)
    return /^https:$/i.test(parsed.protocol)
  } catch {
    return false
  }
}

export const DocumentLinksSection = ({
  links,
  canManageLinks,
  isAddingLink,
  isDeletingLink,
  onAddLink,
  onDeleteLink
}: DocumentLinksSectionProps) => {
  const {t, i18n} = useRiskSurveyTranslation()
  const [url, setUrl] = useState('')
  const [label, setLabel] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)

  const trimmedUrl = url.trim()
  const trimmedLabel = label.trim()

  const urlError = useMemo(() => {
    if (!isSubmitted) return undefined
    if (!trimmedUrl) return t('links.urlInvalid')
    try {
      // Client-side validation mirrors backend checks and keeps feedback immediate.
      if (!isHttpsUrl(trimmedUrl)) return t('links.urlNotHttps')
      return undefined
    } catch {
      return t('links.urlInvalid')
    }
  }, [isSubmitted, t, trimmedUrl])

  const labelError = useMemo(() => {
    if (!isSubmitted) return undefined
    return trimmedLabel ? undefined : t('links.labelRequired')
  }, [isSubmitted, t, trimmedLabel])

  const canSubmit = canManageLinks && isHttpsUrl(trimmedUrl) && !!trimmedLabel

  const handleAdd = () => {
    setIsSubmitted(true)
    // Recompute validity after marking as submitted to avoid using stale state.
    if (!canManageLinks || !isHttpsUrl(trimmedUrl) || !trimmedLabel) return

    onAddLink({url: trimmedUrl, label: trimmedLabel})
    setUrl('')
    setLabel('')
    setIsSubmitted(false)
  }

  return (
    <div className="flex flex-col gap-4" data-cy="report-links-section">
      <div className="flex items-center justify-between gap-2">
        <TextRegular className="font-semibold">{t('links.linksSection')}</TextRegular>
      </div>

      {links.length === 0 ? (
        <TextRegular className="text-textWeak">{t('links.noLinks')}</TextRegular>
      ) : (
        <ul className="flex flex-col gap-3">
          {links.map((link) => (
            <li key={link.id} className="border border-border-subtle rounded px-3 py-2">
              <div className="flex items-start justify-between gap-3">
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 underline break-all"
                >
                  {link.label}
                </a>
                {canManageLinks && (
                  <Button
                    variant="tertiary"
                    onClick={() => onDeleteLink(link.id)}
                    disabled={isDeletingLink}
                    data-cy={`delete-link-${link.id}`}
                  >
                    {t('links.deleteLink')}
                  </Button>
                )}
              </div>
              <TextRegular className="text-textWeak text-sm mt-1">
                {t('links.addedMeta', {
                  user: link.addedByUserName,
                  date: formatDate(link.addedAt, i18n.language)
                })}
              </TextRegular>
            </li>
          ))}
        </ul>
      )}

      {canManageLinks && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
          <ControlledInput
            name="reportLinkUrl"
            label={t('links.urlLabel')}
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            isTouched={isSubmitted}
            error={urlError}
            data-cy="add-link-url"
          />
          <ControlledInput
            name="reportLinkLabel"
            label={t('links.labelLabel')}
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            isTouched={isSubmitted}
            error={labelError}
            data-cy="add-link-label"
          />
          <div className="md:col-span-2">
            <Button
              onClick={handleAdd}
              disabled={!canSubmit || isAddingLink}
              data-cy="add-link-submit"
            >
              {t('links.addLink')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
